import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPrint, FaCheck, FaFilePdf } from 'react-icons/fa';

function RescisaoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rescisao, setRescisao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('token');

  const fetchRescisao = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/rescisao/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar rescisão');
      const data = await response.json();
      setRescisao(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRescisao();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleConfirmarPagamento = async () => {
    if (!window.confirm('Confirmar pagamento desta rescisão?')) return;
    try {
      const response = await fetch(`/api/rescisao/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status: 'PAGO',
          data_pagamento: new Date().toISOString().split('T')[0]
        })
      });
      if (!response.ok) throw new Error('Erro ao confirmar pagamento');
      setSuccess('✅ Pagamento confirmado!');
      fetchRescisao();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getMotivoLabel = (motivo) => {
    const motivos = {
      'DEMISSAO_SEM_JUSTA_CAUSA': 'Demissão sem Justa Causa',
      'DEMISSAO_COM_JUSTA_CAUSA': 'Demissão com Justa Causa',
      'PEDIDO_DEMISSAO': 'Pedido de Demissão',
      'ACORDO': 'Acordo',
      'FIM_CONTRATO': 'Fim de Contrato',
      'APOSENTADORIA': 'Aposentadoria'
    };
    return motivos[motivo] || motivo;
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>❌ {error}</div>;
  if (!rescisao) return <div style={{ textAlign: 'center', padding: '50px' }}>Rescisão não encontrada</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: '16px' }}>
          <FaArrowLeft /> Voltar
        </button>
        <div>
          <button onClick={handlePrint} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>
            <FaPrint /> Imprimir
          </button>
          {rescisao.status === 'CALCULADO' && (
            <button onClick={handleConfirmarPagamento} style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              <FaCheck /> Confirmar Pagamento
            </button>
          )}
        </div>
      </div>

      {success && <div style={{ backgroundColor: '#e8f5e9', padding: '10px', borderRadius: '4px', color: '#2e7d32', marginBottom: '15px' }}>✅ {success}</div>}
      {error && <div style={{ backgroundColor: '#ffebee', padding: '10px', borderRadius: '4px', color: '#c62828', marginBottom: '15px' }}>❌ {error}</div>}

      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} id="termo-rescisao">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>📄 Termo de Rescisão do Contrato de Trabalho</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0' }}>
            {rescisao.funcionario?.nome_completo}
          </p>
        </div>

        {/* Dados do Funcionário */}
        <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #dee2e6' }}>
          <h3>👤 Dados do Funcionário</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <p><strong>Nome:</strong> {rescisao.funcionario?.nome_completo}</p>
            <p><strong>CPF:</strong> {rescisao.funcionario?.cpf || '-'}</p>
            <p><strong>Cargo:</strong> {rescisao.cargo_na_saida || '-'}</p>
            <p><strong>Salário:</strong> {formatCurrency(rescisao.salario_na_saida)}</p>
            <p><strong>Admissão:</strong> {formatDate(rescisao.data_admissao)}</p>
            <p><strong>Saída:</strong> {formatDate(rescisao.data_saida)}</p>
            <p><strong>Motivo:</strong> {getMotivoLabel(rescisao.motivo)}</p>
            <p><strong>Status:</strong> {rescisao.status}</p>
          </div>
        </div>

        {/* Valores */}
        <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #dee2e6' }}>
          <h3>💰 Valores da Rescisão</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <p><strong>Saldo de Salário:</strong> {formatCurrency(rescisao.saldo_salario)}</p>
            <p><strong>Aviso Prévio:</strong> {formatCurrency(rescisao.aviso_previo)}</p>
            <p><strong>Férias Vencidas + 1/3:</strong> {formatCurrency(rescisao.ferias_vencidas + (rescisao.ferias_vencidas / 3))}</p>
            <p><strong>Férias Proporcionais + 1/3:</strong> {formatCurrency(rescisao.ferias_proporcionais + (rescisao.ferias_proporcionais / 3))}</p>
            <p><strong>13º Salário Proporcional:</strong> {formatCurrency(rescisao.decimo_terceiro)}</p>
            <p><strong>Multa FGTS (40%):</strong> {formatCurrency(rescisao.multa_fgts)}</p>
          </div>
        </div>

        {/* Totais */}
        <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div>
              <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>Total Bruto</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                {formatCurrency(rescisao.total_bruto)}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>Total Descontos</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                {formatCurrency(rescisao.total_descontos)}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>Total Líquido</p>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                {formatCurrency(rescisao.total_liquido)}
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #dee2e6' }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
            <strong>Data do Cálculo:</strong> {formatDate(rescisao.data_calculo)}
          </p>
          {rescisao.observacao && (
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
              <strong>Observação:</strong> {rescisao.observacao}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default RescisaoDetalhes;